import { useEffect, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { jwtDecode } from "jwt-decode";

import { supabase } from "@/lib/supabase";
import {
  selectAuthIsRecovery,
  selectAuthPaymentStatus,
  selectAuthUser,
} from "../store/selectors/auth";
import { authorizationUser, logoutUserSuccess, setRecovery } from "../store/auth/slice";
import analytics from "../lib/Analytics";
import { ERoutes } from "../constants/routes";
import { UNSIGNED_USER_ROUTES } from "../constants/constants";
import { JWT, UserStatus } from "../store/auth/types";
import { User } from "../types/auth";
import { fetchSelectedBrandId } from "../store/brands/slice";

export const useAuth = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const isRecovery = useSelector(selectAuthIsRecovery);
  const user = useSelector(selectAuthUser);
  const paymentStatus = useSelector(selectAuthPaymentStatus);

  const [isInitialized, setIsInitialized] = useState(false);
  const [localUser, setLocalUser] = useState<User | null>(null);
  const [provider, setProvider] = useState<string | undefined>(undefined);

  const isRegister = location.pathname.includes(ERoutes.Register);
  const isResetPassword = location.pathname.includes(ERoutes.ResetPassword);
  const isUnsignedUserRoute = UNSIGNED_USER_ROUTES.some((route) =>
    location.pathname === route || location.pathname.startsWith(route)
  );
  const isPaymentFlow = location.pathname === ERoutes.Payment || location.pathname === ERoutes.PaymentSuccess;

  useLayoutEffect(() => {
    supabase.auth
      .getSession()
      .then(({ data }) => data.session)
      .then((session) => {
        if (session) {
          const jwt: JWT = jwtDecode(session.access_token);
          const { user_role, is_subscribed } = jwt;

          setLocalUser(session?.user as unknown as User);
          setProvider(session.user.app_metadata.provider);

          dispatch(fetchSelectedBrandId(session.user.id));

          setIsInitialized(true);

          dispatch(
            authorizationUser({
              user: session.user as unknown as User,
              isRegister,
              isSubscribed: is_subscribed === UserStatus.Subscribed,
              userRole: user_role,
            }),
          );
        } else {
          setProvider(undefined);
          setIsInitialized(true);
          if (!isUnsignedUserRoute) {
            navigate(ERoutes.Login, { replace: true });
          }
        }
      })
      .catch(() => {
        setIsInitialized(true);
        if (!isUnsignedUserRoute) {
          navigate(ERoutes.Login, { replace: true });
        }
      });
  }, []);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        dispatch(setRecovery(true));
        navigate(ERoutes.ResetPassword);
      }

      if (event === "INITIAL_SESSION") {
        if (!isRecovery && isResetPassword) {
          navigate(ERoutes.Login, { replace: true });
        }
      }

      if (event === "SIGNED_OUT") {
        if (!isUnsignedUserRoute) analytics.reset();
        setLocalUser(null);
        setProvider(undefined);
        // Clear Redux auth state so ProtectedRoute redirects immediately
        dispatch(logoutUserSuccess());
        navigate(ERoutes.Login, { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Instant server-side session revocation detection (agency detach, member removal)
  // via Supabase Realtime broadcast — no polling needed.
  useEffect(() => {
    if (!localUser?.id) return;
    const ch = supabase.channel(`user:${localUser.id}`)
      .on('broadcast', { event: 'session_revoked' }, () => {
        supabase.auth.signOut();
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [localUser?.id]);

  const isAuthenticated = (localUser && provider === "email") || user;

  const isAuthReady =
    isInitialized &&
    (provider !== "google" || user != null);

  return {
    isInitialized: isAuthReady,
    isAuthenticated,
    paymentStatus,
    isUnsignedUserRoute,
    isPaymentFlow
  };
};
